// @givmo/assistant-panel — the organization's acceptance, and the once-per-person notice.
//
// ══ THE BODY IS THE SERVER'S, NOT THIS PACKAGE'S ═══════════════════════════════════════════════
//
// Both surfaces render `instrument.content` — the canonical bytes the registry serves — and the
// acceptance sends back the hash of exactly those bytes. That is the whole point of the hash check:
// an organization can only be bound to text it was actually shown, so copy compiled into this package
// would defeat it the moment the registry's version moved. Everything AROUND the body (the title, the
// two buttons, the line saying what accepting does) is chrome and comes from the host's vocabulary.
//
// ⚠ A host whose instrument body already carries its own scope sentence passes `consentScopeLine:
// null` rather than printing it twice.
//
// ══ THE TWO SURFACES ARE NOT THE SAME DISCLOSURE ═══════════════════════════════════════════════
//
//   · The CARD binds the ORGANIZATION, is shown once for the whole organization, and only to someone
//     who can act for it. "Not now" is a real answer and leaves the surface exactly as it was.
//   · The NOTICE tells ONE person, once, what they are about to use. It binds nothing, which is why
//     its dismissal is per-person client state and its only control is an acknowledgement.
//
// Neither is a standing disclosure: once the notice is dismissed the panel carries no fine print at
// all — no footer, no per-reply legend, nothing.

import { Button, Dialog, testIdProp } from '@givmo/design-system';
import type { OrgConsentRow } from './assistant-decode';
import { withOrgName, type AssistantVocab } from './assistant-panel-config';

/**
 * The instrument body, rendered as paragraphs.
 *
 * Plain text with blank-line paragraph breaks — deliberately NOT markdown and deliberately not
 * `dangerouslySetInnerHTML`. This is legal copy whose exact bytes are being hashed and accepted; a
 * renderer that could drop, reflow or reinterpret a character would put the displayed text and the
 * hashed text out of step, and one that could execute markup would let the registry's contents reach
 * the DOM as code. Text nodes only.
 *
 * ⚠ ONE exception, and it changes no bytes: a host may name a phrase (its policy's title) to make
 * CLICKABLE. The phrase is matched literally, only its FIRST occurrence is linked, and the characters
 * rendered are exactly the characters hashed — the transform is presentational. A phrase the
 * instrument does not contain simply produces no link, so a copy revision can never break the render.
 */
function InstrumentBody({
  content,
  className,
  link,
  testId,
}: {
  content: string;
  className: string;
  link: { phrase: string; href: string } | null;
  testId: string;
}) {
  const paragraphs = content.split(/\n\s*\n/).filter((part) => part.trim().length > 0);
  let linked = false;
  return (
    <>
      {paragraphs.map((paragraph, index) => {
        const at =
          link === null || linked || link.phrase.length === 0 ? -1 : paragraph.indexOf(link.phrase);
        if (at < 0 || link === null) {
          return (
            <p key={index} className={className} {...testIdProp(index === 0 ? testId : undefined)}>
              {paragraph}
            </p>
          );
        }
        linked = true;
        return (
          <p key={index} className={className} {...testIdProp(index === 0 ? testId : undefined)}>
            {paragraph.slice(0, at)}
            <a
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="font-w600 text-brand-600 underline underline-offset-2 outline-none focus-visible:shadow-ring"
              {...testIdProp(`${testId}-policy`)}
            >
              {link.phrase}
            </a>
            {paragraph.slice(at + link.phrase.length)}
          </p>
        );
      })}
    </>
  );
}

/** The card's body type ramp, and the notice's — the same copy at two sizes. */
const CARD_PARAGRAPH =
  'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const NOTICE_PARAGRAPH =
  'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-secondary';

export interface AssistantConsentCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instrument: OrgConsentRow;
  orgName: string | null;
  vocab: AssistantVocab;
  /** The host's policy link, wired into the instrument's own policy phrase. */
  policyLink: { phrase: string; href: string } | null;
  onAccept: (contentHash: string) => void;
  accepting: boolean;
  testId: string;
}

/**
 * The organization's acceptance.
 *
 * Nothing has been added around the instrument: no bullet list of data types, no second link, no
 * checkbox, no close cross. The buttons are cancel-then-confirm, matching every other confirm in the
 * suite, and declining simply closes.
 */
export function AssistantConsentCard({
  open,
  onOpenChange,
  instrument,
  orgName,
  vocab,
  policyLink,
  onAccept,
  accepting,
  testId,
}: AssistantConsentCardProps) {
  const title = withOrgName(vocab.consentTitle, orgName);
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content aria-label={title} className="flex flex-col gap-12" testId={testId}>
        <Dialog.Title className="font-sans text-s18 font-w700 leading-h13 tracking-givmo text-text-strong">
          {title}
        </Dialog.Title>
        <InstrumentBody
          content={instrument.content}
          className={CARD_PARAGRAPH}
          link={policyLink}
          testId={`${testId}-body`}
        />
        {vocab.consentScopeLine !== null && (
          <p className={CARD_PARAGRAPH} {...testIdProp(`${testId}-scope`)}>
            {withOrgName(vocab.consentScopeLine, orgName)}
          </p>
        )}
        <div className="mt-12 flex items-center justify-end gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            size="md"
            onClick={() => onOpenChange(false)}
            testId={`${testId}-decline`}
          >
            {vocab.consentDecline}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={accepting}
            onClick={() => onAccept(instrument.contentHash)}
            testId={`${testId}-accept`}
          >
            {vocab.consentAccept}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export interface AssistantFirstUseNoticeProps {
  instrument: OrgConsentRow;
  orgName: string | null;
  vocab: AssistantVocab;
  policyLink: { phrase: string; href: string } | null;
  onDismiss: () => void;
  testId: string;
}

/**
 * The one piece of fine print the panel ever carries, shown once per person and then never again.
 *
 * It sits ABOVE the thread rather than over it: a person who opened the assistant to ask something
 * should be able to read this and start typing without dismissing a modal first.
 */
export function AssistantFirstUseNotice({
  instrument,
  orgName,
  vocab,
  policyLink,
  onDismiss,
  testId,
}: AssistantFirstUseNoticeProps) {
  return (
    <div className="mb-20 rounded-all12 bg-brand-50 p-14" {...testIdProp(testId)}>
      <div className="flex flex-col gap-8">
        <InstrumentBody
          content={instrument.content}
          className={NOTICE_PARAGRAPH}
          link={policyLink}
          testId={`${testId}-body`}
        />
        <p className={NOTICE_PARAGRAPH}>{withOrgName(vocab.noticeScopeLine, orgName)}</p>
      </div>
      <div className="mt-12 flex justify-end">
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={onDismiss}
          testId={`${testId}-dismiss`}
        >
          {vocab.noticeDismiss}
        </Button>
      </div>
    </div>
  );
}

/**
 * The single quiet line shown when the assistant cannot run and this person cannot change that.
 *
 * Two causes, two sentences, and they must not be merged: an organization that has not accepted has
 * someone who CAN turn it on, while an environment with no instrument has nobody who can. Telling a
 * person to go and ask an Owner who has no control to offer them wastes their time and the Owner's.
 */
export function AssistantBlockedLine({ message, testId }: { message: string; testId: string }) {
  return (
    <p
      className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary"
      role="status"
      {...testIdProp(testId)}
    >
      {message}
    </p>
  );
}
